// frontend/src/services/auth.ts
import api from "@/utils/axios";
import i18n from "@/i18n";
import type { Language } from "@/locales/locales";

export type LoginResponse = {
  access_token: string;
  token_type: "bearer" | string;
  user?: Record<string, any>;
};

export type LoginResult = {
  token: string;
  user?: Record<string, any>
};

export type RegisterResponse = {
  status: "created" | "existing_unverified" | "existing_verified";
  message: string;
  verification_email_sent?: boolean;
  admin_notified?: boolean;
};

export type RegisterPayload = {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  turnstile_token: string;
  language?: Language;
};

export type ResendVerificationResponse = { status: "ok" };
export type ForgotPasswordResponse     = { message: string };
export type ResetPasswordResponse      = { message: string };
export type VerifyEmailResponse        = { message: string };

const TOKEN_KEY = "token";

function extractError(err: any, fallback = i18n.t("common.error")): never {
  const msg =
    err?.response?.data?.detail ??
    err?.response?.data?.message ??
    err?.message ??
    fallback;
  throw new Error(String(msg));
}

/**
 * LOGIN — returns { token, user? } on success, THROWS on failure.
 */
export const loginUser = async (email: string, password: string): Promise<LoginResult> => {
  try {
    const { data } = await api.post<LoginResponse>("/api/login", { email, password });
    if (!data?.access_token) {
      throw new Error(i18n.t("auth.login.no_token"));
    }
    return { token: data.access_token, user: data.user };
  } catch (err: any) {
    extractError(err, i18n.t("auth.login.error"));
  }
};

/** Clear auth artifacts from localStorage */
export const logoutUser = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem("user");
  } catch {}
};

/** Read the persisted JWT from localStorage. */
export const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

/**
 * FORGOT PASSWORD — THROWS on failure (e.g., Turnstile/validation).
 */
export const requestPasswordReset = async (
  email: string,
  turnstileToken?: string
): Promise<ForgotPasswordResponse> => {
  try {
    const { data } = await api.post<ForgotPasswordResponse>("/api/auth/forgot-password", {
      email,
      turnstile_token: turnstileToken,
    });
    return data;
  } catch (err: any) {
    extractError(err, i18n.t("auth.forgot_password.error"));
  }
};

/** RESET PASSWORD — THROWS on failure. */
export const performPasswordReset = async (
  token: string,
  password: string
): Promise<ResetPasswordResponse> => {
  try {
    const { data } = await api.post<ResetPasswordResponse>("/api/auth/reset-password", { token, password });
    return data;
  } catch (err: any) {
    extractError(err, i18n.t("auth.reset_password.error"));
  }
};

export const registerUser = async (payload: RegisterPayload): Promise<RegisterResponse> => {
  try {
    const { data } = await api.post<RegisterResponse>("/api/auth/register", payload);
    return data;
  } catch (err) {
    extractError(err, i18n.t("auth.register.error_generic"));
  }
};

export const verifyEmail = async (token: string): Promise<VerifyEmailResponse> => {
  try {
    const { data } = await api.get<VerifyEmailResponse>("/api/auth/verify-email", { params: { token } });
    return data;
  } catch (err: any) {
    extractError(err, i18n.t("auth.verify.error"));
  }
};

export const resendVerification = async (email: string): Promise<ResendVerificationResponse> => {
  try {
    const { data } = await api.post<ResendVerificationResponse>("/api/auth/resend-verification", { email });
    return data;
  } catch (err: any) {
    extractError(err, i18n.t("auth.verify.resend_failed"));
  }
};